/**
 * AppShell — the authenticated layout wrapper.
 *
 * ⚠️  MOBILE-FIRST shell.
 *  • The mobile top bar (`.m-topbar`) + bottom nav (5 slots) define the canonical UX.
 *  • Desktop side rail is a progressive enhancement that mirrors the same navigation.
 *  • Both use `env(safe-area-inset-*)` so iPhone notch + home indicator stay clear.
 *  • Don't add a 6th tab to the mobile bottom nav without redesigning it.
 *  • Don't put critical actions in hover-only states (touch devices have no hover).
 */
import React, { useEffect } from "react";
import { NavLink, Outlet, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { LayoutDashboard, Dumbbell, History, Trophy, Swords, User, Shield, LogOut, House, Play, Flame } from "lucide-react";
import NotificationBell from "./NotificationBell";
import IosInstallBanner from "./IosInstallBanner";
import Onboarding from "./Onboarding";
import { updateProfile } from "../lib/api";

const NAV = [
    { to: "/dashboard",    label: "Dashboard",    short: "Home",   icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/exercises",    label: "Exercises",    short: "Train",  icon: Dumbbell,        testid: "nav-exercises" },
    { to: "/history",      label: "History",      short: "Log",    icon: History,         testid: "nav-history" },
    // 📌 PRODUCT DECISION (user, 2026-06-11): this tab is named "Chart".
    // Do NOT rename back to Compete/Leaderboard/Rank until the user says so.
    { to: "/leaderboard",  label: "Chart",        short: "Chart",  icon: Trophy,          testid: "nav-leaderboard" },
    { to: "/competitions", label: "Challenges",   short: "Arena",  icon: Swords,          testid: "nav-competitions" },
    { to: "/profile",      label: "Profile",      short: "You",    icon: User,            testid: "nav-profile" },
];

export default function AppShell() {
    const { user, loading, signOut } = useAuth();
    const location = useLocation();
    // Compete tab represents the union of /leaderboard + /competitions, so both highlight it.
    const onCompetePath = location.pathname.startsWith("/leaderboard") || location.pathname.startsWith("/competitions");

    // First-run welcome: only for accounts that never worked out. Accounts that
    // predate the flag (production users with history) get it silently marked.
    const needsOnboarding = !!user && !user.onboarded && !user.last_workout_date;
    useEffect(() => {
        if (user && !user.onboarded && user.last_workout_date) {
            updateProfile({ onboarded: true }).catch((err) =>
                console.warn("[shell] silent onboarded mark failed:", err?.message || err));
        }
    }, [user]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" data-testid="app-loading">
                <div className="label-tactical">Loading…</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/" replace />;

    return (
        <div className="app" data-testid="app-shell">
            {needsOnboarding && <Onboarding />}
            <aside className="sidebar">
                <div className="sb-head">
                    <div className="wordmark">
                        <span className="bar" />
                        <span>nerdy</span>
                        <span className="caret">_</span>
                    </div>
                    <NotificationBell />
                </div>
                <nav className="nav">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            data-testid={n.testid}
                            className={({ isActive }) =>
                                `nav-item ${isActive ? "active" : ""}`
                            }
                        >
                            <n.icon size={16} />
                            <span>{n.label}</span>
                            <span className="tag">{n.short}</span>
                        </NavLink>
                    ))}
                    {user.is_admin && (
                        <NavLink
                            to="/admin"
                            data-testid="nav-admin"
                            className={({ isActive }) =>
                                `nav-item root ${isActive ? "active" : ""}`
                            }
                        >
                            <Shield size={16} />
                            <span>Admin</span>
                            <span className="tag">sudo</span>
                        </NavLink>
                    )}
                </nav>
                <div className="sb-user">
                    <div className="who">
                        {user.picture ? (
                            <img src={user.picture} alt="" className="avatar object-cover" />
                        ) : (
                            <div className="avatar">{user.name?.[0] || "U"}</div>
                        )}
                        <div className="min-w-0">
                            <div className="nm truncate" data-testid="sidebar-user-name">{user.name}</div>
                            <div className="em truncate">{user.email}</div>
                        </div>
                    </div>
                    <button onClick={signOut} className="signout" data-testid="sidebar-signout">
                        <LogOut size={14} /> Sign out
                    </button>
                </div>
            </aside>

            {/* Mobile top bar — paddingTop respects iPhone notch / Dynamic Island via safe-area-inset-top. */}
            <div
                className="m-topbar md:hidden"
            >
                <div className="wm">
                    <span className="bar" />
                    <span>nerdy</span>
                </div>
                <div className="right">
                    <span className="m-chip-up"><Flame size={13} />{user.streak_count || 0}d</span>
                    <NotificationBell />
                    <NavLink className="m-av" to="/profile" data-testid="mobile-profile" aria-label="Open profile">{user.name?.[0] || "U"}</NavLink>
                </div>
            </div>

            <IosInstallBanner />

            <main className="main pt-[calc(env(safe-area-inset-top,0px)+5rem)] md:pt-[38px] pb-24 md:pb-20">
                <Outlet />
            </main>

            {/* Mobile bottom nav — paddingBottom respects iPhone home indicator. */}
            <nav className="m-tabbar md:hidden">
                <NavLink to="/dashboard" data-testid="mobile-nav-home" className={({ isActive }) => `m-tab ${isActive ? "on" : ""}`}>
                    <House size={22} /><span>Home</span>
                </NavLink>
                <NavLink to="/exercises" data-testid="mobile-nav-train" className={({ isActive }) => `m-tab ${isActive ? "on" : ""}`}>
                    <Dumbbell size={22} /><span>Train</span>
                </NavLink>
                <div className="m-fab-lbl">
                    <NavLink to="/exercises" data-testid="mobile-start" className="m-fab" aria-label="Start workout">
                        <Play size={26} />
                    </NavLink>
                    <span>Start</span>
                </div>
                <NavLink to="/leaderboard" data-testid="mobile-nav-compete" className={() => `m-tab ${onCompetePath ? "on" : ""}`}>
                    {/* 📌 PRODUCT DECISION (user, 2026-06-11): tab is named "Chart" — keep until user says otherwise. */}
                    <Trophy size={22} /><span>Chart</span>
                </NavLink>
                <NavLink to="/profile" data-testid="mobile-nav-you" className={({ isActive }) => `m-tab ${isActive ? "on" : ""}`}>
                    <User size={22} /><span>You</span>
                </NavLink>
            </nav>
        </div>
    );
}
